/**
 * Quick add-to-cart: intercepts the wrapped product form submit,
 * posts to /cart/add.js with button state feedback (adding → added / error),
 * then refreshes and opens the cart drawer.
 * Falls back to a normal form submit when JS is unavailable.
 */
window.WsQuickAdd = class WsQuickAdd extends HTMLElement {
  #abort = null;
  #busy = false;
  #resetTimer = null;

  connectedCallback() {
    this.#abort = new AbortController();
    this.querySelector('form')?.addEventListener('submit', (event) => this.#submit(event), {
      signal: this.#abort.signal
    });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    clearTimeout(this.#resetTimer);
  }

  async #submit(event) {
    event.preventDefault();
    if (this.#busy) return;

    const form = event.currentTarget;
    const button = form.querySelector('[name="add"]');
    const label = button?.querySelector('[data-add-label], .ws-product-card__button-label') ?? button;
    const errorBox = form.querySelector('[data-form-error]');
    const originalText = label?.textContent;

    this.#busy = true;
    clearTimeout(this.#resetTimer);
    button?.setAttribute('aria-busy', 'true');
    if (errorBox) errorBox.hidden = true;
    if (label && button?.dataset.textAdding) label.textContent = button.dataset.textAdding;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.description || data?.message || `HTTP ${response.status}`);
      }

      button?.setAttribute('data-state', 'added');
      if (label && button?.dataset.textAdded) label.textContent = button.dataset.textAdded;
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
    } catch (error) {
      if (errorBox) {
        errorBox.textContent = error.message || button?.dataset.textError || '';
        errorBox.hidden = false;
      }
      if (label && button?.dataset.textError && !errorBox) label.textContent = button.dataset.textError;
    } finally {
      button?.removeAttribute('aria-busy');
      this.#busy = false;
      this.#resetTimer = setTimeout(() => {
        button?.removeAttribute('data-state');
        if (label && originalText) label.textContent = originalText;
      }, 2000);
    }
  }
}

if (!customElements.get('ws-quick-add')) {
  customElements.define('ws-quick-add', WsQuickAdd);
}
